
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import {namespace} from 'vuex-class';
import {IRoute, IRouteExtraStopPosition, IRouteLeg, IRouteOrder} from '@/model/Route/routeInterfaces';
import {IOnOffHandlers} from 'bonomo/dist';
import {UrlHelper} from '@/helpers/UrlHelper';
import {orderStatusColor} from '@/helpers/orderStatusHelper';
import ServiceProviderBrandIcon from '@/components/shared/chips/ServiceProviderBrandIcon.vue';
import {settings} from '@/plugins/settings';
import RoutePageHeader from '@/components/route/RoutePageHeader.vue';
import RouteLegActions from '@/components/route/RouteLegActions.vue';

const routeModule = namespace('routeModule');

@Component({
  components: {ServiceProviderBrandIcon, RoutePageHeader, RouteLegActions},
})
export default class RouteDetail extends Vue {
  @Prop() protected routeId!: number;

  @routeModule.Getter('route') protected route!: IRoute | null;
  @routeModule.Getter('routeOrders') protected routeOrders!: IRouteOrder[];
  @routeModule.Action('fetchRoute') protected fetchRoute!: (routeId: number) => any;
  @routeModule.Action('deleteRoute') protected deleteRouteAction!: (routeId: number) => any;
  @routeModule.Action('markLegVisited') protected markLegVisitedAction!: (data: {routeId: number; legId: number; isVisited: boolean}) => any;

  protected loading: boolean = true;
  protected showDeleteDialog: boolean = false;
  protected deleting: boolean = false;
  protected togglingLegIds: number[] = [];
  protected ons: IOnOffHandlers = {};

  protected get canAdmin(): boolean {
    return this.$canAction('routes/admin');
  }

  protected get canEdit(): boolean {
    return this.$canAction('routes/edit') || this.$canAction('routes/admin');
  }

  protected get canDelete(): boolean {
    return this.$canAction('routes/delete') || this.$canAction('routes/admin');
  }

  protected get oldBackofficeUrl(): string {
    return UrlHelper.mayorOldRoute(this.routeId);
  }

  protected get routeOrdersGrouped(): { [key: number]: IRouteOrder[] } {
    if (!this.route || !this.route.legs || this.route.legs.length === 0) {
      return {};
    }

    const grouped: { [key: number]: IRouteOrder[] } = {};
    const legsState = this.route.legs.map((leg) => ({
      id: leg.id,
      normalizedAddress: this.normalizeRouteAddress(leg.end_address),
      remaining: Math.max(0, leg.orders_count || 0),
    }));

    this.route.legs.forEach((leg) => {
      grouped[leg.id] = [];
    });

    const assignOrder = (order: IRouteOrder, matcher: (leg: typeof legsState[number]) => boolean): boolean => {
      const leg = legsState.find((candidate) => candidate.remaining > 0 && matcher(candidate));
      if (!leg) {
        return false;
      }

      grouped[leg.id].push(order);
      leg.remaining -= 1;
      return true;
    };

    this.routeOrders.forEach((order) => {
      if (order.route_leg_id !== null && grouped[order.route_leg_id]) {
        grouped[order.route_leg_id].push(order);
        const linkedLeg = legsState.find((leg) => leg.id === order.route_leg_id);
        if (linkedLeg && linkedLeg.remaining > 0) {
          linkedLeg.remaining -= 1;
        }
        return;
      }

      const normalizedLocation = this.normalizeRouteAddress(order.location);
      assignOrder(order, (leg) => normalizedLocation !== '' && normalizedLocation === leg.normalizedAddress);
    });

    this.routeOrders.forEach((order) => {
      if (this.isOrderAlreadyAssigned(order, grouped)) {
        return;
      }

      const normalizedLocation = this.normalizeRouteAddress(order.location);
      assignOrder(order, (leg) =>
        normalizedLocation !== '' &&
        leg.normalizedAddress !== '' &&
        (normalizedLocation.includes(leg.normalizedAddress) || leg.normalizedAddress.includes(normalizedLocation))
      );
    });

    this.routeOrders.forEach((order) => {
      if (this.isOrderAlreadyAssigned(order, grouped)) {
        return;
      }

      assignOrder(order, (leg) => leg.remaining > 0);
    });

    return grouped;
  }

  protected get unassignedRouteOrders(): IRouteOrder[] {
    return this.routeOrders.filter((order) => !this.isOrderAlreadyAssigned(order, this.routeOrdersGrouped));
  }

  protected get legacyExtraStops(): string[] {
    if (!this.route || !this.route.extra_stops) {
      return [];
    }

    if (this.route.extra_stop_positions && this.route.extra_stop_positions.length > 0) {
      return [];
    }

    return this.route.extra_stops;
  }

  protected get positionedExtraStops(): IRouteExtraStopPosition[] {
    if (!this.route || !this.route.extra_stop_positions) {
      return [];
    }

    return this.route.extra_stop_positions;
  }

  protected get visitedLegsCount(): number {
    if (!this.route || !this.route.legs) {
      return 0;
    }

    return this.route.legs.filter((leg) => !!leg.is_visited).length;
  }

  protected get visitedLegsPercent(): number {
    if (!this.route || !this.route.legs || this.route.legs.length === 0) {
      return 0;
    }

    return Math.round((this.visitedLegsCount / this.route.legs.length) * 100);
  }

  protected get nextLegIndex(): number {
    if (!this.route || !this.route.legs) {
      return -1;
    }

    return this.route.legs.findIndex((leg) => !leg.is_visited);
  }

  protected get nextLeg(): IRouteLeg | null {
    return this.nextLegIndex === -1 || !this.route ? null : this.route.legs[this.nextLegIndex];
  }

  protected isNextLeg(leg: IRouteLeg): boolean {
    return !!this.nextLeg && this.nextLeg.id === leg.id;
  }

  protected scrollToLeg(leg: IRouteLeg): void {
    const element = document.getElementById('route-leg-' + leg.id);
    if (element) {
      element.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }

  protected extrasBeforeLeg(beforeLegIndex: number): IRouteExtraStopPosition[] {
    return this.positionedExtraStops.filter((extraStop) => this.extraStopBeforeLegIndex(extraStop) === beforeLegIndex);
  }

  protected legDisplayLetter(index: number): string {
    return String.fromCharCode(65 + index + this.countExtraStopsBeforeLeg(index));
  }

  protected extraStopDisplayLetter(beforeLegIndex: number, extraIndex: number): string {
    return String.fromCharCode(65 + beforeLegIndex + this.countExtraStopsBeforeLeg(beforeLegIndex) + extraIndex);
  }

  protected extraStopBeforeLegIndex(extraStop: IRouteExtraStopPosition): number | null {
    if (extraStop.before_leg_index !== null && extraStop.before_leg_index !== undefined) {
      return extraStop.before_leg_index;
    }

    return null;
  }

  protected countExtraStopsBeforeLeg(beforeLegIndex: number): number {
    return this.positionedExtraStops.filter((extraStop) => {
      const extraBeforeLegIndex = this.extraStopBeforeLegIndex(extraStop);
      return extraBeforeLegIndex !== null && extraBeforeLegIndex < beforeLegIndex;
    }).length;
  }

  protected formatLegDistance(distance: number | null): string | null {
    if (distance === null || distance === undefined) {
      return null;
    }

    if (distance < 1000) {
      return `${Math.round(distance)} m`;
    }

    return `${(distance / 1000).toFixed(1)} km`;
  }

  protected formatLegDuration(duration: number | null): string | null {
    if (duration === null || duration === undefined) {
      return null;
    }

    const totalMinutes = Math.max(1, Math.round(duration / 60));

    if (totalMinutes < 60) {
      return `${totalMinutes} min`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
  }

  protected legMapUrl(leg: IRouteLeg): string | null {
    if (leg.endpoint_lat === null || leg.endpoint_lng === null) {
      return null;
    }

    return UrlHelper.googleMapsPoint(leg.endpoint_lat, leg.endpoint_lng);
  }

  protected extraStopMapUrl(extraStop: IRouteExtraStopPosition): string | null {
    if (!extraStop.lat || !extraStop.lng) {
      return null;
    }

    return UrlHelper.googleMapsPoint(extraStop.lat, extraStop.lng);
  }

  protected phoneHref(phone: string): string {
    return 'tel:' + phone.replace(/[^\d+]/g, '');
  }

  protected ordersForLeg(leg: IRouteLeg): IRouteOrder[] {
    return this.routeOrdersGrouped[leg.id] || [];
  }

  protected orderHasDocument(order: IRouteOrder): boolean {
    return !!order.hasImage;
  }

  protected orderDocumentUrl(order: IRouteOrder): string {
    return `${settings.endpoint}image/orders/${order.id}`;
  }

  protected openOrderDocument(order: IRouteOrder): void {
    window.open(this.orderDocumentUrl(order), '_blank', 'noopener');
  }

  protected normalizeRouteAddress(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    return String(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  protected isOrderAlreadyAssigned(order: IRouteOrder, grouped: { [key: number]: IRouteOrder[] }): boolean {
    for (const legId in grouped) {
      if (grouped[legId].some((candidate) => candidate.id === order.id)) {
        return true;
      }
    }

    return false;
  }

  protected extraStopLetter(idx: number): string {
    const base = this.route && this.route.legs ? this.route.legs.length : 0;
    return String.fromCharCode(65 + base + idx);
  }

  protected orderStatusColor = orderStatusColor;

  protected isOrderDone(order: IRouteOrder): boolean {
    const doneStatuses = [
      'status_order_sp_collected',
      'status_order_sp_delivered',
      'status_order_completed',
    ];
    return doneStatuses.includes(order.status);
  }

  protected isOrderWarning(order: IRouteOrder): boolean {
    return !!order.is_route_warning_order && !this.isOrderDone(order);
  }

  protected isLegToggling(leg: IRouteLeg): boolean {
    return this.togglingLegIds.indexOf(leg.id) !== -1;
  }

  protected onToggleLegVisited(leg: IRouteLeg): void {
    if (this.isLegToggling(leg)) {
      return;
    }
    this.togglingLegIds.push(leg.id);
    this.markLegVisitedAction({routeId: this.routeId, legId: leg.id, isVisited: !leg.is_visited});
  }

  created() {
    this.ons = this.$ons({
      'backend.call.OK.routeLoaded': this.onRouteLoaded,
      'backend.call.KO.routeLoaded': this.onRouteLoaded,
      'backend.call.OK.routeDeleted': this.onRouteDeleted,
      'backend.call.KO.routeDeleted': this.onDeleteError,
      'backend.call.OK.legVisited': this.onLegVisitedSettled,
      'backend.call.KO.legVisited': this.onLegVisitedSettled,
    });
    this.fetchRoute(this.routeId);
  }

  beforeDestroy() {
    this.$offs(this.ons);
  }

  protected onLegVisitedSettled() {
    this.togglingLegIds = [];
  }

  protected onRouteLoaded() {
    this.loading = false;
  }

  protected onDeleteRoute() {
    this.deleting = true;
    this.deleteRouteAction(this.routeId);
  }

  protected onRouteDeleted() {
    this.deleting = false;
    this.showDeleteDialog = false;
    this.$router.push({name: 'routeList'});
  }

  protected onDeleteError() {
    this.deleting = false;
  }
}
